import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Smartphone, Scan, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

/**
 * QR Code Section — Display featured QR code on home page
 */
export function QRCodeSection() {
  const [qrCode, setQrCode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        // Get the first active QR code (you can customize this logic)
        const { data, error } = await supabase
          .from('qr_codes')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (!error && data) {
          setQrCode(data);
        }
      } catch (err) {
        console.error('Failed to fetch QR code:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQRCode();
  }, []);

  // Don't render if no QR code
  if (isLoading || !qrCode) return null;

  return (
    <section className="py-12 sm:py-16 lg:py-20 xl:py-24 bg-gradient-to-br from-brand-red/5 via-brand-white to-brand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-brand-red/10 rounded-full mb-3 sm:mb-4">
              <Scan size={12} className="text-brand-red sm:w-3.5 sm:h-3.5" />
              <span className="text-[10px] sm:text-xs font-bold text-brand-red uppercase tracking-wide">
                Quick Access
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-brand-dark mb-3 sm:mb-4">
              Scan & Order
              <br />
              <span className="text-brand-red">In Seconds</span>
            </h2>

            <p className="text-sm sm:text-base lg:text-lg text-brand-gray mb-4 sm:mb-6 leading-relaxed">
              Skip the wait. Scan our QR code with your phone to instantly access our full menu,
              browse dishes, and place your order directly through WhatsApp.
            </p>

            {/* Features */}
            <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              {[
                { icon: Smartphone, text: 'Works on any smartphone' },
                { icon: QrCode, text: 'No app download required' },
                { icon: ArrowRight, text: 'Direct WhatsApp ordering' },
              ].map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="flex items-center gap-2 sm:gap-3"
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-lg sm:rounded-xl bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-brand-red sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px]" />
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-brand-dark">{feature.text}</p>
                  </motion.div>
                );
              })}
            </div>

            <Link
              to="/menu"
              className="inline-flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 lg:py-3.5 bg-brand-red hover:bg-red-700 text-brand-white text-xs sm:text-sm font-bold rounded-lg sm:rounded-xl transition-all shadow-lg hover:shadow-xl"
            >
              Browse Menu
              <ArrowRight size={14} className="sm:w-4 sm:h-4" />
            </Link>
          </motion.div>

          {/* Right: QR Code */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-brand-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 border border-ui-border">
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 sm:w-24 sm:h-24 bg-brand-red/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 sm:w-32 sm:h-32 bg-brand-red/5 rounded-full blur-3xl" />

              {/* QR Code */}
              <div className="relative">
                <div className="bg-brand-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 border border-ui-border overflow-hidden">
                  <div className="w-full aspect-square">
                    <div
                      dangerouslySetInnerHTML={{ __html: qrCode.qr_data }}
                      className="qr-code-container"
                    />
                  </div>
                </div>

                {/* Scan instruction */}
                <div className="mt-3 sm:mt-4 lg:mt-6 text-center">
                  <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-brand-red/10 rounded-full">
                    <Scan size={12} className="text-brand-red sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
                    <span className="text-[10px] sm:text-xs lg:text-sm font-bold text-brand-dark">
                      Point your camera here
                    </span>
                  </div>
                  <p className="text-[9px] sm:text-[10px] lg:text-xs text-brand-gray mt-1.5 sm:mt-2 lg:mt-3">
                    {qrCode.name} • {qrCode.scan_count || 0} scans
                  </p>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="absolute -bottom-2 sm:-bottom-3 lg:-bottom-4 left-1/2 -translate-x-1/2 bg-success text-brand-white px-2.5 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full shadow-lg"
            >
              <p className="text-[9px] sm:text-[10px] lg:text-xs font-bold flex items-center gap-1 sm:gap-1.5">
                <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-brand-white rounded-full animate-pulse" />
                Active & Ready
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
